import React, { useContext, useState } from 'react';
import { Typography, useTheme, Box } from '@material-ui/core';

import { useMutation, useLazyQuery } from '@apollo/client';
import { usePostHog } from 'posthog-js/react';

import { useParams } from 'react-router-dom';

import { isValidPhoneNumber } from 'react-phone-number-input';

import { useTranslation } from 'react-i18next';

import Styled from './CaregiverInfo.styles';

import { Titles } from '../../components/Titles/Titles';
import {
  TermsAndConditionsAcknowledgements,
} from '../../components/TermsAndConditionsAcknowledgements/TermsAndConditionsAcknowledgements';
import { DataSharingInfo } from '../../components/DataSharingInfo/DataSharingInfo';
import { OnboardingFlowContext } from '../../OnboardingFlowContext';
import { WhyDoWeNeedThisInfo } from '../../components/OnboardingPopover/popoverContent/WhyDoWeNeedThisInfo';
import validateCaregiverInfoForm from './validateCaregiverInfoForm';

import { OnboardingParams } from '../../types';

import { ErrorText } from '../../components/ErrorText/ErrorText';

import { PhoneNumberInput } from '../../components/PhoneNumberInput/PhoneNumberInput';

import { isEmailError } from '../../utils/errorHelpers';

import { Button, TextField } from '@/components';
import { getPdtUid, isSpark as isSparkHelper } from '@/utils/productHelpers';
import { validateEmail } from '@/utils/stringValidators';
import { ENROLL_PATIENT } from '@/apollo/mutations';
import { convertDOBToDateString } from '@/utils/stringUtils';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useUtmParams } from '@/hooks/useUtmParams';

export const CaregiverInfo: React.FC = () => {
  const {
    updateFormData,
    onboardingFormData,
    next,
    uid,
  } = useContext(OnboardingFlowContext);
  useAcceptedLanguage();
  const { utmParams } = useUtmParams();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { product, slug } = useParams<OnboardingParams>();
  const isSpark = isSparkHelper(product);
  const effectiveFormData = isSpark
    ? { ...onboardingFormData, safetyAcknowledgement: true }
    : onboardingFormData;
  const formValid = validateCaregiverInfoForm(effectiveFormData);
  const { t } = useTranslation();
  const theme = useTheme();
  // validate each text field individually. used for error states and helper text...
  const [caregiverFirstNameFieldError, setCaregiverFirstNameFieldError] = useState(false);
  const [caregiverLastNameFieldError, setCaregiverLastNameFieldError] = useState(false);
  const [caregiverPhoneNumberFieldError, setCaregiverPhoneNumberFieldError] = useState(false);
  const [caregiverEmailFieldError, setCaregiverEmailFieldError] = useState(false);
  const [generalErrorMessage, setGeneralErrorMessage] = useState(undefined);

  const [enrollPatient, { loading }] = useMutation(ENROLL_PATIENT);
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, {
    fetchPolicy: 'network-only',
  });
  const posthog = usePostHog();

  const handleEnrollPatient = async () => {
    setGeneralErrorMessage(undefined);
    try {
      const result = await enrollPatient({
        variables: {
          address: (
            onboardingFormData.patientStreetAddress?.trim()?.toLowerCase() === 'null'
            || !onboardingFormData.patientStreetAddress?.trim()
          )
            ? undefined
            : onboardingFormData.patientStreetAddress.trim(),
          city: (
            onboardingFormData.patientCity?.trim()?.toLowerCase() === 'null'
            || !onboardingFormData.patientCity?.trim()
          )
            ? undefined
            : onboardingFormData.patientCity.trim(),
          dateOfBirth: convertDOBToDateString(onboardingFormData.patientDateOfBirth),
          email: onboardingFormData.patientEmail,
          emergencyContactEmail: onboardingFormData.caregiverEmail,
          emergencyContactName: onboardingFormData.caregiverFirstName,
          emergencyContactPhone: onboardingFormData.caregiverPhoneNumber,
          firstName: onboardingFormData.patientFirstName,
          lastName: onboardingFormData.patientLastName,
          organizationSlug: slug,
          pdtUid: getPdtUid(product),
          phone: onboardingFormData.patientPhoneNumber,
          postalCode: (
            onboardingFormData.patientZipCode?.trim()?.toLowerCase() === 'null'
            || !onboardingFormData.patientZipCode?.trim()
          )
            ? undefined
            : onboardingFormData.patientZipCode.trim(),
          state: (
            onboardingFormData.patientState?.trim()?.toLowerCase() === 'null'
            || !onboardingFormData.patientState?.trim()
          )
            ? undefined
            : onboardingFormData.patientState.trim(),
          uid,
          isDependent: true,
          ...utmParams,
        },
      });

      if (result?.data?.enrollDirectAccessPatient?.success === true) {
        // creating the user in our system was successful

        try {
          const { data: idData } = await getDistinctId({
            variables: { email: onboardingFormData.patientEmail },
          });

          if (idData?.posthogDistinctIdWithEmail?.distinctId) {
            posthog?.identify(idData.posthogDistinctIdWithEmail.distinctId, {
              email: onboardingFormData.patientEmail,
            });
          }
        } catch (error) {
          console.error('Error fetching PostHog distinct ID:', error);
        }

        next();
      } else if (isEmailError(result?.errors)) {
        setCaregiverEmailFieldError(true);
      } else {
        // creating the user in our system was UNsuccessful
        console.warn('we were not able to successfully create a profile for this user: ', result);
        setGeneralErrorMessage(t('coverage.errors.generic_error'));
      }
    } catch (e) {
      // There was a server error
      console.log('there was a server error trying to create this user', e);
      setGeneralErrorMessage(t('coverage.errors.generic_error'));
    }
  };

  return (
    <>
      <Titles
        title={t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_title`)}
      />
      <Styled.HoverContainer
        onClick={() => setPopoverOpen(!popoverOpen)}
      >
        <Typography>
          <u>{t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_subtitle`)}</u>
        </Typography>
      </Styled.HoverContainer>
      <Typography variant="h2" style={{ color: theme.palette.grey[800] }}>
        {t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_info_title`)}
      </Typography>

      <TextField
        required
        error={caregiverFirstNameFieldError}
        helperText={caregiverFirstNameFieldError ? t('coverage.errors.first_name_error') : ''}
        label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_f_name_label`)}
        id="caregiverFirstName"
        inputProps={{
          placeholder: t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_f_name_label`),
        }}
        value={onboardingFormData.caregiverFirstName}
        onChange={(e) => {
          setCaregiverFirstNameFieldError(false);
          updateFormData({ caregiverFirstName: e.target.value });
        }}
        onBlur={(e) => {
          setCaregiverFirstNameFieldError(e.target.value === '');
        }}
      />
      <TextField
        required
        error={caregiverLastNameFieldError}
        helperText={caregiverLastNameFieldError ? t('coverage.errors.last_name_error') : ''}
        label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_l_name_label`)}
        id="caregiverLastName"
        inputProps={{
          placeholder: t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_l_name_label`),
        }}
        value={onboardingFormData.caregiverLastName}
        onChange={(e) => {
          setCaregiverLastNameFieldError(false);
          updateFormData({ caregiverLastName: e.target.value });
        }}
        onBlur={(e) => {
          setCaregiverLastNameFieldError(e.target.value === '');
        }}
      />
      <TextField
        required
        error={caregiverEmailFieldError}
        helperText={caregiverEmailFieldError ? t('coverage.errors.email_error') : ''}
        label={t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_email_label`)}
        id="caregiverEmail"
        inputProps={{
          placeholder: t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_email_label`),
        }}
        value={onboardingFormData.caregiverEmail}
        onChange={(e) => {
          setCaregiverEmailFieldError(false);
          updateFormData({ caregiverEmail: e.target.value });
        }}
        onBlur={(e) => {
          setCaregiverEmailFieldError(!validateEmail(e.target.value));
        }}
      />
      <PhoneNumberInput
        placeholder={t(`coverage.${product}.${onboardingFormData.personEnrolling}.caregiver_phone_label`)}
        value={onboardingFormData.caregiverPhoneNumber}
        onChange={(value) => {
          setCaregiverPhoneNumberFieldError(false);
          updateFormData({ caregiverPhoneNumber: value || '' });
        }}
        id="caregiverPhoneNumber"
        onBlur={() => {
          setCaregiverPhoneNumberFieldError(!isValidPhoneNumber(onboardingFormData.caregiverPhoneNumber));
        }}
        error={caregiverPhoneNumberFieldError}
        helperText={caregiverPhoneNumberFieldError ? t('coverage.errors.phone_error') : ''}
      />

      <div
        style={{ marginBottom: '32px' }}
      />
      <TermsAndConditionsAcknowledgements
        safetyAcknowledgement={onboardingFormData.safetyAcknowledgement}
        safetyAcknowledgementLabel={t('coverage.safety_acknowledgement_label')}
        policyAndTermsAcknowledgement={onboardingFormData.policyAndTermsAcknowledgement}
        onSelect={(data) => updateFormData(data)}
      />
      {generalErrorMessage && (
        <ErrorText error={generalErrorMessage} />
      )}
      <Box sx={{ width: '100%', justifyItems: 'center', marginTop: 24 }}>
        <Button
          data-testid="submit-button"
          onClick={handleEnrollPatient}
          disabled={formValid === false}
          loading={loading}
          style={{
            borderRadius: 100,
          }}
        >
          {t('coverage.next')}
        </Button>
      </Box>
      <DataSharingInfo />
      <WhyDoWeNeedThisInfo
        personEnrolling={onboardingFormData.personEnrolling}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
      />
    </>
  );
};
